/**
 * TEMP-C-003: Single Source Stage API Service
 * 
 * Consolidated API service for stage thresholds, personas, and QC data.
 * Provides single source of truth for frontend QC display.
 */

import type { 
  StageType
} from '@/types/execution';
import { getApiHeaders } from '@/lib/api-base';

// Stage name normalization utility
function normalizeStage(stage: string): string {
  // Handle legacy 'video' -> 'videos' mapping
  return stage === 'video' ? 'videos' : stage;
}

// Single source API types aligned with backend
export interface PersonaThresholds {
  personaSlug: string;
  passMin: number;
  fallbackMin: number;
  categoryThresholds: Record<string, number>;
  retryLimit: number;
}

export interface StageSettings {
  executionId: string;
  stage: string;
  version: string;
  qcPersonaSlug: string;
  categoryThresholds: Record<string, number>;
  passMin: number;
  fallbackMin: number;
  retryLimit: number;
  updatedAt: string;
  updatedBy?: string;
  isDefaultFromLaunch: boolean;
}

export interface QCScoreData {
  execution_id: string;
  stage: StageType;
  target_stage: string;
  persona_slug: string;
  rubricHash: string;
  weighted_score: number;
  category_scores: Record<string, number>;
  passed: boolean;
  borderline?: boolean;
  hard_fail_categories?: string[];
  evaluated_at: string;
}

export interface StageIteration {
  id: string;
  execution_id: string;
  stage: string;
  iteration_number: number;
  decision: 'accept' | 'improve' | 'rewrite';
  scores?: Record<string, number>;
  feedback?: string;
  improved_elements?: string[];
  preserved_elements?: string[];
  persona_name?: string;
  persona_model?: string;
  persona_action?: string;
  is_best_attempt: boolean;
  trigger_type: 'ai_evaluation' | 'user_feedback';
  accepted_iteration?: boolean; // New field for approval tracking
  created_at: string;
}

export interface StageDisplayData {
  settings: StageSettings;
  thresholds: PersonaThresholds;
  iterations: StageIteration[];
  acceptedIteration?: StageIteration;
  currentScore?: QCScoreData;
}

class StageAPIService {
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_BASE_URL || 'https://api.hi-si.ai';
  }

  /**
   * Get all stage data for display - single source endpoint
   */
  async getStageDisplayData(executionId: string, stage: string): Promise<StageDisplayData> {
    // Normalize stage name to handle legacy mappings
    const normalizedStage = normalizeStage(stage);
    
    try {
      const headers = getApiHeaders();
      const [thresholdsRes, iterationsRes, qcRes] = await Promise.all([
        // Consolidated thresholds
        fetch(`${this.baseURL}/executions/${executionId}/stages/${normalizedStage}/thresholds`, {
          headers,
          credentials: 'include'
        }),
        // Consolidated iterations
        fetch(`${this.baseURL}/executions/${executionId}/stages/${normalizedStage}/iterations`, {
          headers,
          credentials: 'include'
        }),
        // Consolidated QC summary
        fetch(`${this.baseURL}/executions/${executionId}/stages/${normalizedStage}/qc-summary`, {
          headers,
          credentials: 'include'
        })
      ]);

      if (!thresholdsRes.ok || !iterationsRes.ok) {
        throw new Error(`Failed to fetch stage data: ${thresholdsRes.status} ${iterationsRes.status}`);
      }

      const [thresholdsData, iterationsData, qcData] = await Promise.all([
        thresholdsRes.json(),
        iterationsRes.json(),
        qcRes.ok ? qcRes.json() : null
      ]);

      // Map consolidated thresholds response
      const settings: StageSettings = {
        executionId,
        stage: normalizedStage,
        version: thresholdsData.version || 'v1',
        qcPersonaSlug: thresholdsData.qcPersonaSlug,
        categoryThresholds: thresholdsData.categoryThresholds || {},
        passMin: Number(thresholdsData.passMin),
        fallbackMin: Number(thresholdsData.fallbackMin),
        retryLimit: Number(thresholdsData.retryLimit),
        updatedAt: thresholdsData.updatedAt || new Date().toISOString(),
        updatedBy: thresholdsData.updatedBy,
        isDefaultFromLaunch: thresholdsData.isDefaultFromLaunch ?? true
      };

      // Convert to PersonaThresholds format
      const thresholds: PersonaThresholds = {
        personaSlug: thresholdsData.qcPersonaSlug,
        passMin: Number(thresholdsData.passMin),
        fallbackMin: Number(thresholdsData.fallbackMin),
        categoryThresholds: thresholdsData.categoryThresholds || {},
        retryLimit: Number(thresholdsData.retryLimit)
      };

      // Process iterations
      const iterations: StageIteration[] = (iterationsData.items || [])
        .map((it: any) => ({
          id: `${executionId}-${normalizedStage}-${it.iterationNumber}`,
          execution_id: executionId,
          stage: normalizedStage,
          iteration_number: Number(it.iterationNumber),
          decision: it.accepted ? 'accept' : 'improve',
          scores: {},
          feedback: undefined,
          improved_elements: [],
          preserved_elements: [],
          persona_name: undefined,
          persona_model: it.modelUsed,
          persona_action: undefined,
          is_best_attempt: it.accepted || false,
          trigger_type: 'ai_evaluation',
          accepted_iteration: it.accepted,
          created_at: it.createdAt
        }));

      // Find accepted iteration
      const acceptedIteration = iterations.find(iter => iter.accepted_iteration);

      // Current QC score data
      const currentScore: QCScoreData | undefined = qcData ? {
        execution_id: executionId,
        stage: normalizedStage as StageType,
        target_stage: normalizedStage,
        persona_slug: qcData.personaSlug || thresholds.personaSlug,
        rubricHash: qcData.rubricHash || 'default',
        weighted_score: Number(qcData.weightedScore || 0),
        category_scores: qcData.subScores || {},
        passed: qcData.decision === 'pass',
        borderline: qcData.decision === 'borderline',
        hard_fail_categories: [],
        evaluated_at: qcData.updatedAt || new Date().toISOString()
      } : undefined;

      return {
        settings,
        thresholds,
        iterations,
        acceptedIteration,
        currentScore
      };

    } catch (error) {
      console.error('Failed to fetch stage display data:', error);
      throw error;
    }
  }

  /**
   * Get all stages data for execution
   */
  async getAllStagesDisplayData(executionId: string): Promise<Record<string, StageDisplayData>> {
    const stages = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'elements', 'frames', 'videos', 'assemble', 'publish'];
    const results: Record<string, StageDisplayData> = {};

    try {
      const stagePromises = stages.map(stage => 
        this.getStageDisplayData(executionId, stage)
          .then(data => ({ stage, data }))
          .catch(error => ({ stage, error }))
      );

      const stageResults = await Promise.all(stagePromises);

      stageResults.forEach(result => {
        if ('data' in result) {
          results[result.stage] = result.data;
        } else {
          console.warn(`Failed to load stage ${result.stage}:`, result.error);
        }
      });

      return results;
    } catch (error) {
      console.error('Failed to fetch all stages data:', error);
      throw error;
    }
  }

  /**
   * Approve/accept a specific iteration
   */
  async approveIteration(executionId: string, stage: string, iterationNumber: number): Promise<void> {
    // Normalize stage name to handle legacy mappings
    const normalizedStage = normalizeStage(stage);
    
    try {
      const response = await fetch(`${this.baseURL}/iterations/${executionId}/${normalizedStage}/${iterationNumber}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          ...getApiHeaders(),
        },
        body: JSON.stringify({ approved: true })
      });

      if (!response.ok) {
        throw new Error(`Failed to approve iteration: ${response.status}`);
      }

      console.log(`Approved iteration ${iterationNumber} for stage ${normalizedStage}`);
    } catch (error) {
      console.error('Failed to approve iteration:', error);
      throw error;
    }
  }

  /**
   * Get persona information for stage
   */
  getPersonaInfo(personaSlug: string): { name: string; role: string; description: string } {
    // Map persona slugs to display information
    const personaMap: Record<string, { name: string; role: string; description: string }> = {
      'eval_script_narrative_quality': {
        name: 'The Editor',
        role: 'Script Quality Evaluator',
        description: 'Evaluates narrative coherence and storytelling quality'
      },
      'eval_vision_thematic_alignment': {
        name: 'The Architect',
        role: 'Vision Quality Evaluator', 
        description: 'Ensures thematic consistency and visual language'
      },
      'eval_storyboard_visual_flow': {
        name: 'The Director',
        role: 'Storyboard Evaluator',
        description: 'Assesses visual flow and composition'
      },
      'eval_shots_technical_feasibility': {
        name: 'The Operator',
        role: 'Shots Quality Evaluator',
        description: 'Reviews technical feasibility and shot planning'
      },
      'eval_continuity_consistency': {
        name: 'The Inspector',
        role: 'Continuity Evaluator',
        description: 'Maintains visual and narrative consistency'
      },
      'eval_elements_visual_quality': {
        name: 'The Artist',
        role: 'Elements Quality Evaluator',
        description: 'Evaluates visual elements and composition'
      },
      'eval_frames_style_adherence': {
        name: 'The Critic',
        role: 'Frames Quality Evaluator',
        description: 'Ensures style adherence and visual quality'
      },
      'eval_video_production_quality': {
        name: 'The Producer',
        role: 'Video Quality Evaluator',
        description: 'Assesses production quality and motion'
      },
      'eval_assemble_technical_quality': {
        name: 'The Engineer',
        role: 'Assembly Quality Evaluator',
        description: 'Reviews technical assembly and integration'
      },
      'eval_publish_distribution_ready': {
        name: 'The Publisher',
        role: 'Publishing Evaluator', 
        description: 'Ensures distribution readiness and quality'
      }
    };

    return personaMap[personaSlug] || {
      name: 'Unknown Persona',
      role: 'Quality Evaluator',
      description: `Evaluates ${personaSlug}`
    };
  }
}

// Export singleton instance
export const stageAPI = new StageAPIService();
export default stageAPI;